import { useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { ProfileDetails } from './components/ProfileDetails';
import { OpeningHours } from './components/OpeningHours';
import Icon, { IconName } from '../../components/Icon';
import UserBadge, { UserBadgeStyle } from './components/UserBadge';
import { canManageMembers, formatDistance, Pleb } from '../../models/Pleb';
import { plebsRepository } from '../../repositories/PlebsRepository';
import { useAuth } from '../../hooks/useAuth';
import { useBlockedUsers } from '../../hooks/useBlockedUsers';
import Loader from './components/Loader';
import { ConnectionButton } from './components/ConnectionButton';
import { useRouter } from '../../hooks/useRouter';
import { useTheme } from '../../hooks/useTheme';
import { useConfirm } from '../../hooks/useConfirm';
import Slider from 'react-slick';
import { Button } from '../../components/Button';
import { ButtonSize } from '../../components/Button';
import { ButtonVariant } from '../../config/colors';
import { LayoutWithTopBar } from '../../components/LayoutWithTopBar';
import { ButtonSendTo } from '../../components/ButtonSendTo';
import { CopyToClipboard } from '../../components/CopyToClipboard';
import { eventsRepository } from '../../repositories/EventsRepository'; 
import { Event, FormatDate } from '../../models/Event';
import Clickable from '../../components/Clickable';
import { ConnectionTypes } from '../../models/Connection';
import { connectionRepository } from '../../repositories/ConnectionRepository';
import { formatAmountWithSATS } from '../../utils/Utils';

interface ProfilePageUserProps {}

function formatSlugChangedAt(changedAt: string): string | null {
  const changedTime = new Date(changedAt).getTime();

  if (Number.isNaN(changedTime)) {
    return null;
  }

  const elapsedMs = Math.max(Date.now() - changedTime, 0);
  const days = Math.floor(elapsedMs / 86_400_000);

  if (days > 0) {
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  }

  const hours = Math.max(Math.floor(elapsedMs / 3_600_000), 1);
  return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
}

export const ProfilePageUser: React.FC<ProfilePageUserProps> = () => {
  const { userId } = useParams<{ userId: string }>();
  const {pleb} = useAuth();
  const {onUserBlocked, onUserUnblocked} = useBlockedUsers();
  const [user, setUser] = useState<Pleb | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [distance, setDistance] = useState<string | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const navigate = useNavigate();
  const {navigateFromFullChannelId, navigateToEvent, navigateToCall} = useRouter();
  const {isDarkMode} = useTheme();
  const {openConnectDialog} = useConfirm();
  const {isMobile} = useTheme();
  const {openConfirm} = useConfirm();

  const refreshUser = () => {
    plebsRepository.getUserDetails(userId ?? '', pleb?.id ?? '')
      .then((user) => {
        setUser(user);
        if(user && user.distance) {
          setDistance(formatDistance(user.distance));
        }
      })
      .catch((error) => {
        console.error('Error fetching user:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    if(userId === pleb?.id) {
      if(pleb)
        setUser(new Pleb(pleb));
      else
        setUser(null);
      setDistance(null);
      setIsLoading(false);
    }
    else
    {
      setIsLoading(true);
      setUser(null);
      setDistance(null);
      refreshUser();
    }

    eventsRepository.getEventsCreatedByUser(userId ?? '', pleb?.id ?? '', 0, 10)
      .then((events) => {
        setEvents(events.data);
      })
      .catch((error) => {
        console.error('Error fetching events:', error);
      });
  }, [userId, pleb]);

  const handleClickConnect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if(user)
      openConnectDialog(user.toJson());
  }

  const banUser = (id: string) => {
    openConfirm({
      message: 'Are you sure you want to ban this user?',
      buttons: [
          {
            label: 'Ban', 
            onClick: () => {
              plebsRepository.banUser(id)
                .then(() => {
                  setUser(null);
                })
            }, 
            variant: ButtonVariant.danger
          },
          {
            label: 'Cancel', 
            variant: ButtonVariant.transparent
          },
      ]
    });
  }

  const unblockUser = (id: string) => {
    openConfirm({
      message: 'Are you sure you want to unblock this user?',
      buttons: [
          {
            label: 'Unblock', 
            onClick: () => {
              connectionRepository.unblockUser(id)
                .then(() => {
                  onUserUnblocked(id);
                  setIsLoading(true);
                  refreshUser();
                })
            }, 
            variant: ButtonVariant.danger
          },
          {
            label: 'Cancel', 
            variant: ButtonVariant.transparent
          },
      ]
    });
  }

  const blockUser = (id: string) => {
    openConfirm({
      message: 'Are you sure you want to block this user? You won\'t be able to see or contact each other',
      buttons: [
          {
            label: 'Block', 
            onClick: () => {
              connectionRepository.blockUser(id)
                .then(() => {
                  onUserBlocked(id);
                  setIsLoading(true);
                  refreshUser();
                })
            }, 
            variant: ButtonVariant.danger
          },
          {
            label: 'Cancel', 
            variant: ButtonVariant.transparent
          },
      ]
    });
  }

  const sliderSettings = {
    dots: true,
    infinite: false
  };

  const showSlugChangedWarning = Boolean(
    user?.slugChanged?.previousSlug && user?.slugChanged?.changedAt
  );
  const slugChangedRelative = showSlugChangedWarning && user?.slugChanged?.changedAt
    ? formatSlugChangedAt(user.slugChanged.changedAt)
    : null;

  return (
    <LayoutWithTopBar
      /*topBarEndPart={
        (userId !== pleb?.id) && (
          <div className="flex flex-row items-center gap-1">
              <Clickable 
                  className="cursor-pointer flex items-center justify-center rounded-xl h-[50px]" 
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    if(userId)
                        navigateToCall();
                }}
                style={{ width: isMobile ? '35px' : '50px'}}
            >
                <span className="material-icons text-brand-orange text-[30px]">call</span>
            </Clickable>
          </div>
        )
      }*/
      onClickPrevArrow={() => navigate(-1)}
      /*topBarChildren={
        <div className="flex items-center">
          <h1 className="text-l font-bold text-center">{user?.name}</h1>
          {user?.badge && <div className="ml-2"><UserBadge badge={user.badge} /></div>}
        </div>
      }*/
    >
      {
        user && (
          <div className="max-w-[460px] w-full mx-auto py-6">
            <div className="rounded-lg bg-brand-bg-4 overflow-hidden" style={{ maxWidth: '100vw' }}>
              <div className="relative">
                <div>
                  <Slider {...sliderSettings}>
                    {
                      user.pictures.map((picture, index) => (
                        <div key={index}>
                          <img src={picture} alt={user.name} className="w-full aspect-square object-cover" />
                        </div>
                      ))
                    }
                  </Slider>
                </div>
                {user.badges && user.badges.length > 0 && (
                  <div className="absolute top-4 right-4 flex flex-col gap-2">
                    {user.badges.map((badge) => (
                      <UserBadge badge={badge} style={UserBadgeStyle.big} />
                    ))}
                  </div>
                )}
                <div className="absolute bottom-4 left-4">
                  <Button 
                    variant={ButtonVariant.primary} 
                    size={ButtonSize.xsmall} 
                    className="gap-1 rounded-xl"
                  >
                    <CopyToClipboard toCopy={user.shareLink ?? ''} copiedText="Copied">
                      <div className="flex items-center gap-2 px-2">
                        <span className="material-symbols-outlined text-[14px]">ios_share</span>
                        <span>Share</span>
                      </div>
                    </CopyToClipboard>
                  </Button>
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">{user.name}</h2>
                    <p className="">{user.formattedSlug}</p>
                  </div>
                  <div className="flex justify-end items-center gap-2">
                    <ButtonSendTo 
                        pleb={user}
                        width={30}
                    />
                    {user.twitterSlug && (
                      <a 
                        href={`https://x.com/${user.twitterSlug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon 
                          name={IconName.TWITTER} 
                          className="w-6 h-6" 
                          color={isDarkMode ? '#FFFFFF' : '#000000'}
                        />
                      </a>
                    )}
                    {
                        user.connection &&
                          <ConnectionButton 
                            connection={user.connection}
                            onConnect={handleClickConnect}
                            onChat={(connectionId) => {
                              navigateFromFullChannelId(connectionId);
                            }}
                            allowPressAwaitingReply={(pleb && canManageMembers(pleb)) ? true : false}
                          />
                    }
                    {
                      userId === pleb?.id &&
                        <Button
                          variant={ButtonVariant.inverted}
                          size={ButtonSize.small}
                          className="gap-1 rounded-xl"
                          onClick={() => {
                            if(isMobile)
                              navigate('/home/edit-profile');
                            else
                              navigate('/home/edit-profile/user/' + userId);
                          }}
                        >
                          Edit profile
                        </Button>
                    }
                  </div>
                </div>

                {showSlugChangedWarning && user.slugChanged && (
                  <div className="mt-4 flex gap-2 rounded-xl bg-brand-bg-2 p-3">
                    <span className="material-icons text-brand-orange text-[20px] shrink-0">warning</span>
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-semibold uppercase">
                        This user recently changed their username
                      </p>
                      <p className="text-sm">
                        Previously @{user.slugChanged.previousSlug} → Now @{user.slug}
                      </p>
                      {slugChangedRelative && (
                        <p className="text-sm opacity-80">
                          Changed {slugChangedRelative}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <hr className="my-3" />

                {
                  distance && (
                    <div className="mb-4 italic">
                      {distance} away
                    </div>
                  )
                }

                {/* Bio Section */}
                {user.bio && (
                  <div className="prose max-w-none mb-6">
                    {user.bio}
                  </div>
                )}

                {/* Profile Details */}
                <ProfileDetails user={user} />

                {/* Opening Hours */}
                {user.openingTimes && Object.keys(user.openingTimes).length > 0 && (
                  <OpeningHours openingTimes={user.openingTimes} />
                )}

                {
                  pleb && canManageMembers(pleb) && (
                    <div className="mt-6">
                      Membership: {user.subscriptionPeriod}
                    </div>
                  )
                }

                {
                  pleb && canManageMembers(pleb) && user.wallet?.balanceMsat !== undefined && (
                    <div className="mt-6">
                      Balance: {formatAmountWithSATS(user.wallet?.balanceMsat / 1000, true)}
                    </div>
                  )
                }

                {
                  pleb && canManageMembers(pleb) && (
                    <div className="mt-6">
                      App: {
                        user.wallet?.platform?.toLowerCase() === 'web'
                          ? user.wallet.platform
                          : [
                              user.wallet?.platform,
                              user.wallet?.appVersion,
                            ].filter(Boolean).join(' - ') || 'Unknown'
                      }
                    </div>
                  )
                }
              </div>

              {
                events.length > 0 && <div className="px-6 pb-6">
                  <h2 className="text-xl font-bold mb-2">Events ({events.length})</h2>
                  <div className="flex flex-col gap-3">
                    {
                      events.map((event) => (
                        <Clickable 
                          key={event.id} 
                          className="flex flex-row gap-2 rounded-md" 
                          onClick={() => navigateToEvent(event.id)}
                        >
                          <img src={event.image} alt={event.name} className="w-[80px] h-[80px] aspect-square object-cover rounded-lg shrink-0" />
                          <div>
                            <span className="text-[10px] font-semibold shrink-1 rounded-sm px-1 bg-white text-black">ORGANIZER</span>
                            <h3 className='font-semibold'>{event.name}</h3>
                            <div className="text-sm">
                              {event.getDateFormatted(FormatDate.dateHour)}
                            </div>
                          </div>
                        </Clickable>
                      ))
                    }
                  </div>
                </div>
              }

              <div className="px-6 pb-6 flex justify-center">
                {
                  user.connection 
                    && user.connection.type === ConnectionTypes.BLOCKED 
                    && user.connection.blockedByMe ? (
                      <Button 
                      variant={ButtonVariant.transparent} 
                      size={ButtonSize.small} 
                      onClick={() => unblockUser(user.id)}
                      className="underline"
                    >
                      Unblock
                    </Button>
                  ) : (
                    <Button 
                      variant={ButtonVariant.transparent} 
                      size={ButtonSize.small} 
                      onClick={() => blockUser(user.id)}
                      className="underline"
                    >
                      Block
                    </Button>
                  )
                }
                {
                  pleb && canManageMembers(pleb) && (
                    <Button 
                      variant={ButtonVariant.transparent} 
                      size={ButtonSize.small} 
                      onClick={() => banUser(user.id)}
                      className="underline"
                    >
                      Ban user
                    </Button>
                  )
                }
                {
                  pleb && canManageMembers(pleb) && user.coordinates && (
                    <Button 
                      variant={ButtonVariant.transparent} 
                      size={ButtonSize.small} 
                      href={`https://www.google.com/maps/@${user.coordinates.latitude},${user.coordinates.longitude},9z`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Map
                    </Button>
                  )
                }
              </div>
            </div>
          </div>
        )
      }
      {
        !user && !isLoading && (
          <div className="max-w-[460px] w-full mx-auto py-6">
            <div className="text-center">
              <div className='text-xl font-bold'>An error occurred</div>
            </div>
          </div>
        )
      }
      {
        isLoading && (
          <Loader />
        )
      }
    </LayoutWithTopBar>
  );
};

export default ProfilePageUser;